'use strict';

window.app.controller('controller', controller);

controller.$inject = ["$scope", "$http", "$timeout", "$q", "$httpParamSerializer", "$location"];

/**
 * Detail Controller
 *
 * Controlador para la página pública de búsqueda de reservas (sin sesión).
 * Comparte la misma lógica de edición que profile/booking-controller a través
 * de window.__mt.fn.onEditReservation (edit-booking.js).
 *
 * Flujo:
 *  1. El usuario introduce email + nº reserva en el formulario de búsqueda.
 *  2. Se llama a searchReservation() → POST a api.booking (endpoint existente).
 *  3. La respuesta (estructura de API externa) se normaliza al mismo formato
 *     que usa booking_customer_new, para reutilizar el mismo partial/card.
 *  4. vm.reservations se puebla y se muestra home/partials/booking_detail_card.
 *  5. Al pulsar Editar/Ver más → vm.toggleEdit() → vm.isEdit = true → profile/edit.
 */
function controller($scope, $http, $timeout, $q, $httpParamSerializer, $location) {
    var vm = this;
    var setting = window.__mt.setting;
    var WEB_CLIENT_B2B_ID = 106906;

    // ─── Estado compartido con edit-booking.js ───────────────────────────────
    // Estas variables deben estar inicializadas ANTES de que edit-booking.js
    // las use, ya que se inyectan en el mismo vm del controlador.
    vm.ln = window.__mt.ln;
    vm.reservations = [];
    vm.updating = false;

    // Requeridas por edit-booking.js (onEditReservation)
    vm.car = {};
    vm.allComments = [];
    vm.showChangePassagers = false;
    vm.totalPassages = 0;
    vm.isRequiredSelectVehicle = false;
    vm.typePickup = "";
    vm.typeDropoff = "";

    vm.msn = {
        selected: '',
        messages: {
            airport: window.__mt.ln["msn_searchbox_date_pickup"],
            address: window.__mt.ln["msn_searchbox_date_dropoff"],
            default: window.__mt.ln["msn_searchbox_pickuptime"]
        },
        pickup: {
            display: "none",
            msn: "",
        },
        dropoff: {
            display: "none",
            msn: "",
        },
    };

    vm.message = {
        selected: '',
        pickup: {
            display: "none",
            msn: "",
        },
        dropoff: {
            display: "none",
            msn: "",
        },
    };

    vm._search_confirm = {
        from: '',
        from_view: '',
        to: '',
        to_view: '',
        adults: 1,
        children: 0,
        infants: 0,
        search_from: { id: 1, lat: null, lng: null },
        search_to: { id: 1, lat: null, lng: null },
    };

    // ─── Estado propio del formulario de búsqueda ────────────────────────────
    vm.searched = false;   // true tras el primer intento de búsqueda
    vm.alert = false;      // true cuando la API no encuentra la reserva
    vm.formTouched = false;
    vm.formatInvalid = false; // true cuando el nº parece un intento de reserva "DIS" mal formado

    vm.searchForm = {
        email: '',
        booking_number: ''
    };

    vm.isWebReservation = function (reservation) {
        var clientB2B = Number(reservation && reservation.client_b2b);
        return clientB2B === WEB_CLIENT_B2B_ID;
    };

    // A booking only counts as agency-made when we have a known, different
    // client_b2b — a missing/null value (e.g. MyDisposition responses never
    // include it) must NOT be treated as "agency" by default.
    vm.isAgencyBooking = function (reservation) {
        if (!reservation || reservation.client_b2b === null || reservation.client_b2b === undefined || reservation.client_b2b === '') {
            return false;
        }
        var clientB2B = Number(reservation.client_b2b);
        return !isNaN(clientB2B) && clientB2B !== WEB_CLIENT_B2B_ID;
    };

    // ─── Validación del formulario ───────────────────────────────────────────
    function isValidEmail(email) {
        var re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(String(email).toLowerCase());
    }

    // Only enforce the "DIS" per-hour format when MyDisposition is enabled
    // (window.__mt.setting.disposition_active, from config('app.disposition_active')) — mirrors the
    // "DIS" prefix check the backend uses (MyTransferServices::GetReservationEmail) to route a booking
    // number to the MyDisposition API instead of a regular booking lookup. If the user is clearly
    // attempting that format (starts with "dis"), enforce it strictly instead of letting a malformed
    // id silently fail as "not found".
    function looksLikeDisAttempt(bookingNumber) {
        return !!(setting && setting.disposition_active) && /^dis/i.test(bookingNumber);
    }
    function isValidDisFormat(bookingNumber) {
        return /^dis-*\d+$/i.test(bookingNumber);
    }

    function isFormValid() {
        return vm.searchForm.booking_number.trim() !== ''
            && isValidEmail(vm.searchForm.email);
    }

    /**
     * Normaliza la respuesta del api.booking (estructura de API externa)
     * al mismo formato de campos que usa booking_detail_card.blade.php.
     *
     * La respuesta de la API tiene esta forma:
     * { status: 200, booking: { customerFirstName, bookedDetails: [...], driverInfo, ... } }
     *
     * Cada bookedDetail representa un tramo. En la página detail solo se busca
     * una reserva concreta, así que normalizamos el primer (y único) detalle.
     */    function normalizeBooking(apiResponse) {
        var booking = apiResponse.booking || {};
        var details = (booking.bookedDetails || [])[0] || {};
        var transfer = details.bookedTransfer || booking.bookedTransfer || {};
        var driverInfo = booking.driverInfo || null;
        var totalPrice = booking.totalPrice || {};
        // client_b2b_id (nuestra base local) es la fuente confiable; clientId de la
        // API externa queda solo como respaldo si la reserva no está en Booking.
        var clientB2B = booking.client_b2b_id || booking.clientId || details.clientId || details.client_b2b_id || null;
        var hasInsurance = Boolean(booking.insurance || details.insurance);
        var isDisBooking = (booking.bookingType === 'DisBooking');

        // Nombre del conductor: la API lo devuelve como string único en driverInfo.driverName
        var driverName = driverInfo ? (driverInfo.driverName || '') : '';
        var driverParts = driverName.split(' ');

        return {
            // ── Identificadores ─────────────────────────────────────────────
            Id: details.bookingReference || String(booking.orderId || ''),
            email: booking.customerEmail || '',

            // ── Cliente ──────────────────────────────────────────────────────
            customer_firstname: booking.customerFirstName || '',
            customer_lastname: booking.customerLastName || '',

            // ── Fechas ───────────────────────────────────────────────────────
            pickup_datetime: details.pickUpDate || '',
            pickup_flight_datetime_track: details.pickup_flight_datetime_track || booking.pickup_flight_datetime_track || null,
            arrival_flight_datetime_track: details.arrival_flight_datetime_track || booking.arrival_flight_datetime_track || null,

            // ── Estado ───────────────────────────────────────────────────────
            status: details.status || 'confirmed',

            // ── Vehículo ─────────────────────────────────────────────────────
            img: transfer.imageURL || '',

            // ── Ruta ─────────────────────────────────────────────────────────
            origin_address: details.pickup || '',
            destination_address: details.dropoff || '',

            // ── Precio ───────────────────────────────────────────────────────
            price_client: totalPrice.totalPrice || details.totalPrice || 0,
            client_b2b: clientB2B,
            insurance: hasInsurance,
            has_insurance: hasInsurance,

            // ── Tipos de punto (airport / address / train_station / port) ────
            from_type: details.pickupType || '',
            to_type: details.dropoffType || '',

            // ── Info de vuelo llegada ─────────────────────────────────────────
            pickup_trans_loc: details.arrivalTransportLocator || '',
            pickup_trans_date: details.arrivalTransportDate || '',

            // ── Info de vuelo salida ──────────────────────────────────────────
            dropoff_trans_loc: details.departureTransportLocator || '',
            dropoff_trans_date: details.departureTransportDate || '',

            // ── Conductor ────────────────────────────────────────────────────
            driver_first_name: driverParts[0] || '',
            driver_last_name: driverParts.slice(1).join(' ') || '',
            driver_dialcode_phone_business: driverInfo ? (driverInfo.phoneNumberCode || '') : '',
            driver_business_phone: driverInfo ? (driverInfo.phoneNumber || '') : '',

            // ── Vehículo asignado ─────────────────────────────────────────────
            vehicle_manufacturer: driverInfo ? (driverInfo.vehicleBrand || '') : '',
            vehicle_model: '',
            vehicle_registration: driverInfo ? (driverInfo.plateNumber || '') : '',

            // ── Flags de edición ─────────────────────────────────────────────
            editable: details.editable !== false,
            isDisBooking: isDisBooking,

            // ── Horas (solo DisBooking) ────────────────────────────────────
            hours: details.hours || null,

            // ── Raw ──────────────────────────────────────────────────────────
            _raw: apiResponse
        };
    }

    // ─── Búsqueda de reserva ─────────────────────────────────────────────────
    vm.searchReservation = function () {
        vm.alert = false;
        vm.formatInvalid = false;
        vm.formTouched = true;

        var bookingNumber = vm.searchForm.booking_number.trim();
        if (looksLikeDisAttempt(bookingNumber) && !isValidDisFormat(bookingNumber)) {
            vm.formatInvalid = true;
            return;
        }

        if (!isFormValid()) {
            return;
        }

        vm.updating = true;
        vm.searched = true;
        vm.reservations = [];

        var params = {
            id: vm.searchForm.booking_number.trim(),
            email: vm.searchForm.email.trim(),
            isUser: false
        };

        $http
            .post(setting.api_booking, params)
            .then(onSearchSuccess, onSearchError);
    };

    function onSearchSuccess(response) {
        vm.updating = false;
        var data = response.data;

        if (data.status === 200 && data.booking) {
            vm.reservations = [normalizeBooking(data)];
        } else {
            vm.alert = true;
            vm.reservations = [];
        }

        $timeout(function () {
            $('[data-toggle="tooltip"]').tooltip();
        }, 100);
    }

    function onSearchError() {
        vm.updating = false;
        vm.alert = true;
    }

    // ─── Callback que usa edit-booking.js para refrescar tras guardar ────────
    function refreshReservation() {
        if (vm.searched) {
            vm.searchReservation();
        }
    }

    // ─── Inicialización ──────────────────────────────────────────────────────
    vm.init = function () {
        // Soporte de deep-link: /detail?email=x@x.com&id=291
        // Se lee window.location.search directamente porque Angular no usa html5Mode
        var qs = new URLSearchParams(window.location.search);
        var email = qs.get('email');
        var id = qs.get('id');
        if (email && id) {
            vm.searchForm.email = email;
            vm.searchForm.booking_number = id;
            vm.searchReservation();
        }
    };

    // ─── Plug-in de edición (compartido con profile) ─────────────────────────
    window.__mt.fn.onEditReservation(vm, $scope, $http, $timeout, $q, refreshReservation, $location);
    window.__mt.fn.onInvoice(vm, $httpParamSerializer, $http, $location, $scope, $timeout, refreshReservation);
}
